import '../style/TableList.css';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/** Services */
import { fetchTableList, createTableFetch, deleteTableFetch, openSessionFetch, closeSessionFetch, getRestaurant, getSessionId } from '../../services/api';
import QrCodeScannerModal from '../modals/QrCodeScannerModal';

import { fullError, showSuccessToast, listenMessage, INACTIVITY_LIMIT } from '../../services/util';
import sound from '../../assets/bell.wav';
import TimerButton from '../buttons/TimerButton';

import PageVisibilityWatcher from '../common/PageVisibilityWatcher';

function TableList(props) {
    const firstFetchTableDone = useRef(false);
    const [tableMap, setTableMap] = useState({});
    const { t } = useTranslation();
    const [componentReady, setComponentReady] = useState(false);

    var [tables, setTables] = useState([]);
    var [tableCount, setTableCount] = useState(-1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentTableObj, setCurrentTableObj] = useState(false);

    const isListenWebsocket = false;


    useEffect(() => {
        if (componentReady && (!tables || tableCount === -1)) {
            fetchTableData(false);
        }
    }, [tables, componentReady]);


    useEffect(() => {
        // Assicurati che tutte le proprietà siano state settate
        if (props.isAdmin !== undefined && componentReady === false) {
            setComponentReady(() => {
                return true;
            });
        }
    }, [props.isAdmin, componentReady]);

    const closeModal = () => {
        setIsModalOpen(false);
    };
    const fetchTableData = async (notify = true) => {
        try {
            props.setShowLoading(true);
            const tableList = await fetchTableList();

            const tempTableList = [];
            const newTableMap = {};

            tableList.data.forEach(table => {
                const tempTable = {
                    ...table,
                    name: t('table') + ' ' + table.number,
                    assigned: (!!table.users && table.users.length > 0)
                };
                newTableMap[tempTable.name + '-' + tempTable._id + '-' + ((tempTable.assigned === true) ? 'assigned' : 'not-assigned')] = tempTable;
                tempTableList.push(tempTable);
            });

            const newKeys = Object.keys(newTableMap);
            const oldKeys = Object.keys(tableMap); // ⬅️ this is the CORRECT previous state

            if (firstFetchTableDone.current) {
                const newTableKeys = newKeys.filter(key => !oldKeys.includes(key));
                if (newTableKeys.length > 0) {
                    newTableListEffect(false);
                }
            }

            firstFetchTableDone.current = true;

            setTables(tempTableList);
            setTableMap(newTableMap); // ⬅️ aggiorna la mappa per confronti futuri
            setTableCount(tempTableList.length);
        } catch (error) {
            fullError(t('anErrorOccurredDuringTablesLoading'), error);
        } finally {
            props.setShowLoading(false);
        }
    };
    useEffect(() => {
        props.setPageTo('tableList');

        if (isListenWebsocket) {
            /** Websocket */
            listenMessage(async () => {
                fetchTableData();
            }, 'one_table_closing_' + getRestaurant(), 'oneTableClosed', false);
            listenMessage(async () => {
                fetchTableData();
            }, 'one_table_opening_' + getRestaurant(), 'oneTableOpened', false);
        }
    }, [props.setPageTo]);

    const newTableListEffect = (notify = true) => {
        if (notify) {
            playSound();
            showSuccessToast(t('TheTableListHasChanged'));
        }
    };
    const playSound = () => {
        try {
            const audio = new Audio(sound);
            audio.play().catch(error => {
                console.error("Errore nella riproduzione dell'audio:", error);
            });
        } catch (error) {
            console.error("Errore generale nel caricamento dell'audio:", error);
        }
    };

    const createTable = async () => {
        try {
            props.setShowLoading(true);

            const tableNumber = tableCount + 1;

            const tableFetch = await createTableFetch(tableNumber, 4);
            if (tableFetch.result === 'OK') {
                setTableCount(tableNumber);
                fetchTableData();
                showSuccessToast(t('tableCreatedSuccessfully'));
            } else {
                fullError(t('anErrorOccurredDuringTableCreation'), tableFetch);
            }
        } catch (error) {
            fullError(t('anErrorOccurredDuringTableCreation'), error);
        } finally {
            props.setShowLoading(false);
        }
    }
    const acceptance = async (tableObj, userId) => {
        try {
            props.setShowLoading(true);

            const tableFetch = await openSessionFetch(tableObj.number, [userId]);
            if (tableFetch.result === 'OK') {
                fetchTableData(false);
                showSuccessToast(t('tableAssignedSuccessfully'));
            } else {
                fullError(t('anErrorOccurredDuringTableAssignment'), tableFetch);
            }
        } catch (error) {
            if (error.response.data.msg === "One or more user already in an opened session") {
                fullError(t('tableAlreadyAssigned'), error);
            } else {
                fullError(t('anErrorOccurredDuringTableAssignment'), error);
            }

        } finally {
            props.setShowLoading(false);
        }
    }
    const preModalProcess = (tableObj) => {
        setCurrentTableObj(tableObj);

        setIsModalOpen(true);
    }
    const closeTable = async (tableObj) => {
        if (await window.confirm(t('confirmTableClosing'))) {
            try {
                props.setShowLoading(true);
                const tableFetch = await closeSessionFetch(tableObj.session);
                if (tableFetch.result === 'OK') {
                    // sendMessage('table_closing_' + tableObj.session, 'isClosed');
                    fetchTableData(false);
                    showSuccessToast(t('tableClosedSuccessfully'));
                } else {
                    fullError(t('anErrorOccurredDuringTableClosure'), tableFetch);
                }

            } catch (error) {
                fullError(t('anErrorOccurredDuringTableClosure'), error);
            } finally {
                props.setShowLoading(false);
            }

        }
    }
    const deleteTable = async (tableObj) => {
        if (await window.confirm(t('confirm'))) {
            try {
                props.setShowLoading(true);

                const tableFetch = await deleteTableFetch(tableObj._id);
                if (tableFetch.result === 'OK') {
                    fetchTableData(false);
                    showSuccessToast(t('tableCancelledCorrectly'));
                } else {
                    fullError(t('anErrorOccurredDuringTableDeletion'), tableFetch);
                }
            } catch (error) {
                fullError(t('anErrorOccurredDuringTableDeletion'), error);
            } finally {
                props.setShowLoading(false);
            }
        }
    }

    return (
        <>
            <PageVisibilityWatcher delayMs={INACTIVITY_LIMIT} onReturn={fetchTableData} />
            <a id="admin-section" className="adminSection"></a>
            <div className="text-center">
                {Object.keys(tables).length === 0 && <h4>
                    {t('TheListIsEmpty')}
                </h4>}
                <TimerButton onClick={fetchTableData}><i className="bi bi-arrow-repeat me-2"></i>{t('update')}</TimerButton>
            </div>
            <div className="container list mt-3 mb-5">
                <QrCodeScannerModal obj={currentTableObj} showLoading={props.showLoading} acceptance={acceptance}
                    isModalOpen={isModalOpen} closeModal={closeModal} />
                {
                    tables.map((tableObj) => (
                        <div key={tableObj._id} className="d-flex align-items-center customerRow">
                            <div className="d-flex align-items-center customerBox">
                                <img alt="" className="table" src={'img/svg/table.svg'} />
                                <div className="me-2 name table text-nowrap">{tableObj.name}</div>
                                {tableObj.assigned &&
                                    <div className="d-flex align-items-center flex-column flex-sm-row customerBox">
                                        {tableObj.users.map((u) => (
                                            <div key={'user-' + (u._id || u) + '-' + tableObj._id} className="me-2 text-center">
                                                <i className="bi bi-person-fill ms-1 me-1 icon"></i>
                                                <div className="ms-2 w-100 name">{u.username || u}</div>
                                            </div>
                                        ))}
                                    </div>
                                }
                            </div>
                            <div className="confirmBox d-flex justify-content-end align-items-center w-100">
                                {!tableObj.assigned
                                    ? (
                                        <button onClick={() => preModalProcess(tableObj)} type="button"
                                            className="btn btn-primary me-1 bi bi-qr-code">
                                            <span className="d-none d-sm-block">{t('assign')}</span>
                                        </button>
                                    )
                                    : (
                                        <div className="d-flex">
                                            <button onClick={() => closeTable(tableObj)} type="button"
                                                className="btn btn-danger me-1 ms-2 bi bi-x">
                                                <span className="d-none d-sm-block">{t('close')}</span>
                                            </button>
                                        </div>
                                    )}
                            </div>
                        </div>
                    ))
                }

                {!props.isStaff ?
                    <>
                        {tables.length > 0 && (

                            <button onClick={() => deleteTable(tables[tables.length - 1])} type="button"
                                className="btn btn-danger me-1 bi bi-trash w-100 mt-3">
                                <span className="d-block">{t('deleteOneTable')}</span>
                            </button>
                        )}
                        <div className="position-fixed ordinaContainer orderListBottomMenu py-1 tableListBottomBox">
                            <div className="d-flex w-100 justify-content-center align-self-stretch">
                                <button onClick={() => createTable()} type="button" className="btn btn-primary">
                                    {t('createTable')}
                                </button>
                            </div>
                        </div>
                    </>
                    :
                    <>
                        <div className="position-fixed ordinaContainer orderListBottomMenu py-1 tableListBottomBox">
                        </div>
                    </>

                }
            </div>
        </>
    );
}
export default TableList;